import api from './api';

/**
 * Servicio para gestión de temporadas
 * 
 * Permite obtener información sobre temporadas activas y gestionar
 * el estado de las temporadas en el sistema
 */
class TemporadaService {
  
  /**
   * Obtiene la temporada activa actual
   * @returns {Promise<Object>} Respuesta con la temporada activa
   */
  async getTemporadaActiva() {
    try {
      const response = await api.get('/postgres/temporadas/activa');
      return {
        success: true,
        data: response.data.data || null,
        message: response.data.message || 'Temporada activa obtenida exitosamente'
      };
    } catch (error) {
      console.error('Error al obtener temporada activa:', error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.message || 'Error al obtener temporada activa'
      };
    }
  }

  /**
   * Obtiene todas las temporadas con filtro opcional
   * @param {Object} filters - Filtros opcionales (activa: true/false)
   * @returns {Promise<Object>} Respuesta con las temporadas
   */
  async getTemporadas(filters = {}) {
    try {
      const params = new URLSearchParams();
      
      if (filters.activa !== undefined) {
        params.append('activa', filters.activa);
      }
      
      const url = `/postgres/temporadas${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await api.get(url);
      
      return {
        success: true,
        data: response.data.data || [],
        count: response.data.count || 0,
        message: response.data.message || 'Temporadas obtenidas exitosamente'
      };
    } catch (error) {
      console.error('Error al obtener temporadas:', error);
      return {
        success: false,
        data: [],
        count: 0,
        error: error.response?.data?.message || 'Error al obtener temporadas'
      };
    }
  }

  /**
   * Obtiene información sobre la temporada automática basada en fecha actual
   * @returns {Promise<Object>} Información de temporada automática
   */
  async getInfoTemporadaAutomatica() {
    try {
      const response = await api.get('/postgres/temporadas/info-automatica');
      return {
        success: true,
        data: response.data.data || null,
        message: response.data.message || 'Información de temporada automática obtenida'
      };
    } catch (error) {
      console.error('Error al obtener información de temporada automática:', error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.message || 'Error al obtener información de temporada automática'
      };
    }
  }

  /**
   * Obtiene categorías de temporadas activas
   * @returns {Promise<Object>} Respuesta con categorías activas
   */
  async getCategoriasActivas() {
    try {
      const response = await api.get('/postgres/temporadas/categorias-activas');
      return {
        success: true,
        data: response.data.data || [],
        count: response.data.count || 0,
        message: response.data.message || 'Categorías activas obtenidas exitosamente'
      };
    } catch (error) {
      console.error('Error al obtener categorías activas:', error);
      return {
        success: false,
        data: [],
        count: 0,
        error: error.response?.data?.message || 'Error al obtener categorías activas'
      };
    }
  }

  /**
   * Obtiene estadísticas de temporadas activas
   * @returns {Promise<Object>} Respuesta con estadísticas
   */
  async getEstadisticasTemporadasActivas() {
    try {
      const response = await api.get('/postgres/temporadas/estadisticas');
      return {
        success: true,
        data: response.data.data || {},
        message: response.data.message || 'Estadísticas obtenidas exitosamente'
      };
    } catch (error) {
      console.error('Error al obtener estadísticas de temporadas activas:', error);
      return {
        success: false,
        data: {},
        error: error.response?.data?.message || 'Error al obtener estadísticas'
      };
    }
  }

  /**
   * Formatea el nombre de una temporada para mostrar
   * @param {Object} temporada - Objeto temporada
   * @returns {string} Nombre formateado
   */
  formatearNombreTemporada(temporada) {
    if (!temporada) return 'Sin temporada';
    return temporada.nombre || `${temporada.ano_inicio}-${temporada.ano_fin}`;
  }

  /**
   * Verifica si una temporada está activa
   * @param {Object} temporada - Objeto temporada
   * @returns {boolean} True si está activa
   */
  isTemporadaActiva(temporada) {
    return temporada && temporada.activa === true;
  }

  /**
   * Obtiene el año de inicio de una temporada
   * @param {Object} temporada - Objeto temporada
   * @returns {number|null} Año de inicio
   */
  getAnoInicio(temporada) {
    return temporada ? temporada.ano_inicio : null;
  }

  /**
   * Obtiene el año de fin de una temporada
   * @param {Object} temporada - Objeto temporada
   * @returns {number|null} Año de fin
   */
  getAnoFin(temporada) {
    return temporada ? temporada.ano_fin : null;
  }

  /**
   * Activa o desactiva una temporada específica
   * @param {number} id - ID de la temporada
   * @param {boolean} activa - Estado deseado
   */
  async toggleTemporada(id, activa) {
    try {
      const response = await api.patch(`/postgres/temporadas/${id}/toggle`, { activa });
      return {
        success: true,
        data: response.data.data || null,
        message: response.data.message || `Temporada ${activa ? 'activada' : 'desactivada'} exitosamente`
      };
    } catch (error) {
      console.error('Error al cambiar estado de temporada:', error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.message || 'Error al cambiar estado de temporada'
      };
    }
  }

  /**
   * Activa automáticamente la temporada que corresponde a la fecha actual
   * y desactiva las demás
   */
  async actualizarAutomaticamente() {
    try {
      const response = await api.post('/postgres/temporadas/actualizar-automaticamente');
      return {
        success: true,
        data: response.data.data || null,
        message: response.data.message || 'Temporadas actualizadas automáticamente'
      };
    } catch (error) {
      console.error('Error al actualizar temporadas automáticamente:', error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.message || 'Error al actualizar automáticamente'
      };
    }
  }

  /**
   * Crea una nueva temporada
   * @param {Object} datos - { nombre, ano_inicio, ano_fin, mes_inicio, mes_fin }
   */
  async createTemporada(datos) {
    try {
      const response = await api.post('/postgres/temporadas', datos);
      return {
        success: true,
        data: response.data.data || null,
        message: response.data.message || 'Temporada creada exitosamente'
      };
    } catch (error) {
      console.error('Error al crear temporada:', error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.message || 'Error al crear temporada'
      };
    }
  }
}

// Exportar una instancia del servicio
const temporadaService = new TemporadaService();
export default temporadaService;
